import { useEffect, useState } from "react";
import { format } from "date-fns";
import cc from "classcat";
import BaseChart from "../../Charts/BaseChart";
import { getTimestamp } from "../../Charts/utils";
import type { ChartDatum } from "../../Charts/types";
import type { NetWorthPeriod } from "./hooks";
import NetWorthMonthlyList from "./NetWorthMonthlyList";
import styles from "./NetWorthTrendChart.module.scss";

type NetWorthTrendChartProps = {
  /** Only used to reset stale hover state when the period changes. */
  period: NetWorthPeriod;
  chartData: ChartDatum[];
  windowStart: Date;
  windowEnd: Date;
  isLoading: boolean;
  error: unknown;
  isSmall: boolean;
  /** Shows that month's gains/losses. Omit to make monthly list rows non-clickable. */
  onSelectMonth?: (datum: ChartDatum) => void;
};

/**
 * Shows just the month ("Jun") except where the year changes from the
 * previous tick (or on the very first tick), where it shows the month and
 * year on separate lines (["Jun", "2026"]) so the year doesn't widen the tick.
 */
export const formatNetWorthTick = (
  tick: Date | number | string,
  index: number,
  ticks: (Date | number | string)[]
): string | string[] => {
  const timestamp = getTimestamp(tick);
  if (timestamp === null) return "";

  const date = new Date(timestamp);
  const previousTimestamp = index > 0 ? getTimestamp(ticks[index - 1]) : null;
  const previousYear =
    previousTimestamp === null
      ? null
      : new Date(previousTimestamp).getFullYear();
  const showYear = previousYear === null || date.getFullYear() !== previousYear;

  return showYear
    ? [format(date, "MMM"), format(date, "yyyy")]
    : format(date, "MMM");
};

const NetWorthTrendChart = ({
  period,
  chartData,
  windowStart,
  windowEnd,
  isLoading,
  error,
  isSmall,
  onSelectMonth,
}: NetWorthTrendChartProps) => {
  const [activeDatum, setActiveDatum] = useState<ChartDatum | null>(null);

  // Stale hover state from the previous period's chartData shouldn't carry
  // over — reset so the list defaults to highlighting the most recent month.
  useEffect(() => {
    setActiveDatum(null);
  }, [period]);

  return (
    <div>
      <div
        className={cc([
          styles.chartLayout,
          isSmall && styles.chartLayoutStacked,
        ])}
      >
        <BaseChart
          // timePeriod only matters as a fallback when tickFormat isn't set;
          // the explicit format below is used instead, so this value is unused.
          timePeriod="year"
          tickFormat={formatNetWorthTick}
          // One data point per month, so tick one per point rather than the
          // default evenly-spaced ticks (which can land multiple ticks within
          // the same month on shorter windows, producing duplicate labels).
          tickValues={chartData.map(({ x }) => x)}
          multilineTickLabels
          chartData={chartData}
          windowStart={windowStart}
          windowEnd={windowEnd}
          isLoading={isLoading}
          isSmall={isSmall}
          error={error}
          showAxisLine
          showValueAxis
          hideTooltip
          onActiveDatumChange={setActiveDatum}
          // The widget's width varies a lot (up to spanning both dashboard
          // columns), so the default aspect-ratio-locked height would look
          // squished at some widths and excessive at others. A fixed pixel
          // target keeps it visually consistent regardless of width.
          chartHeightPx={isSmall ? undefined : 240}
          // The multiline tick format can show a 4-digit year on the first
          // or last tick, and since tickValues lands ticks exactly on the
          // domain's edges here, a centered label at either edge would
          // otherwise clip against the chart's boundary.
          anchorEdgeTickLabels
        />
        <NetWorthMonthlyList
          chartData={chartData}
          activeDatum={activeDatum}
          onSelectMonth={onSelectMonth}
        />
      </div>
    </div>
  );
};

export default NetWorthTrendChart;
